import { Check, ExternalLink, Globe, Mail, RefreshCw, Store, Trash2, User } from "lucide-react";
import { useState, useEffect, useCallback } from "react";
import {
    getEmailEnrichmentTodos,
    approveEmailEnrichment,
    rejectEmailEnrichment,
    purgeEnrichmentSuggestion,
    EmailEnrichmentTodoItem,
} from "../../../interfaces/events";

function extractEmailString(data: any): string | null {
    if (!data) return null;
    if (typeof data === "string") return data;
    if (typeof data === "object") {
        return data.email || data.value || data.address || null;
    }
    return null;
}

function getDaysAgo(dateStr?: string): string | null {
    if (!dateStr) return null;
    const days = Math.floor((Date.now() - new Date(dateStr).getTime()) / (1000 * 60 * 60 * 24));
    if (days === 0) return "aujourd'hui";
    if (days === 1) return "il y a 1 jour";
    return `il y a ${days} jours`;
}

function buildEmailOptions(item: EmailEnrichmentTodoItem): any[] {
    const opts: any[] = [];
    if (item.emailData) {
        const emailStr = extractEmailString(item.emailData);
        if (emailStr) opts.push(item.emailData);
    }
    for (const o of item.emailOptions || []) {
        const email = extractEmailString(o);
        if (email && !opts.some((x) => extractEmailString(x) === email)) {
            opts.push(o);
        }
    }
    return opts.sort((a, b) => ((b as any).score ?? 0) - ((a as any).score ?? 0));
}

function ScoreBadge({ score }: { score?: number }) {
    if (score === undefined || score === null) return null;
    const color =
        score >= 70 ? "bg-green-100 text-green-700 border-green-200" :
        score >= 50 ? "bg-amber-100 text-amber-700 border-amber-200" :
        "bg-red-100 text-red-700 border-red-200";
    return (
        <span className={`inline-flex items-center px-1.5 py-0.5 text-[10px] font-semibold border ${color}`}>
            {score}
        </span>
    );
}

const SCORE_THRESHOLDS: { label: string; value: number | undefined }[] = [
    { label: "Tous", value: undefined },
    { label: "≥30", value: 30 },
    { label: "≥50", value: 50 },
    { label: "≥70", value: 70 },
    { label: "≥80", value: 80 },
];

export default function TodoEmailEnrichment() {
    const [items, setItems] = useState<EmailEnrichmentTodoItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [actionLoading, setActionLoading] = useState<string | null>(null);

    // Filters
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [minScore, setMinScore] = useState<number | undefined>(undefined);

    // Per-card selections: eventId -> option index (-1 = none)
    const [businessSel, setBusinessSel] = useState<Record<string, number>>({});
    const [ownerSel, setOwnerSel] = useState<Record<string, number>>({});
    // eventId -> personId selected for owner email
    const [ownerPersonSel, setOwnerPersonSel] = useState<Record<string, string>>({});

    const fetchItems = useCallback(async () => {
        setLoading(true);
        try {
            const params: any = { page, limit: 50 };
            if (dateFrom) params.from = dateFrom;
            if (dateTo) params.to = dateTo;
            if (minScore !== undefined) params.minScore = minScore;
            const res = await getEmailEnrichmentTodos(params);
            setItems(res.data);
            setTotal(res.meta.total);
            setBusinessSel({});
            setOwnerSel({});
            setOwnerPersonSel({});
        } catch (err) {
            console.error("Failed to fetch email enrichment todos:", err);
        } finally {
            setLoading(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, dateFrom, dateTo, minScore]);

    useEffect(() => {
        fetchItems();
    }, [fetchItems]);

    const removeItem = (eventId: string) => {
        setItems((prev) => prev.filter((i) => i.eventId !== eventId));
        setTotal((prev) => Math.max(0, prev - 1));
        setBusinessSel((prev) => { const next = { ...prev }; delete next[eventId]; return next; });
        setOwnerSel((prev) => { const next = { ...prev }; delete next[eventId]; return next; });
        setOwnerPersonSel((prev) => { const next = { ...prev }; delete next[eventId]; return next; });
    };

    const handleApprove = async (item: EmailEnrichmentTodoItem) => {
        const allOpts = buildEmailOptions(item);
        const bIdx = businessSel[item.eventId] ?? -1;
        const oIdx = ownerSel[item.eventId] ?? -1;
        if (bIdx === -1 && oIdx === -1) return;

        setActionLoading(`${item.eventId}-approve`);
        try {
            const payload: { businessOption?: any; ownerOption?: any; ownerPersonId?: string } = {};
            if (bIdx >= 0 && allOpts[bIdx]) payload.businessOption = allOpts[bIdx];
            if (oIdx >= 0 && allOpts[oIdx]) {
                payload.ownerOption = allOpts[oIdx];
                const personId = ownerPersonSel[item.eventId];
                if (personId) payload.ownerPersonId = personId;
            }
            await approveEmailEnrichment(item.eventId, payload);
            removeItem(item.eventId);
        } catch (err) {
            console.error("Failed to approve email enrichment:", err);
        } finally {
            setActionLoading(null);
        }
    };

    const handleReject = async (item: EmailEnrichmentTodoItem) => {
        setActionLoading(`${item.eventId}-reject`);
        try {
            await rejectEmailEnrichment(item.eventId);
            removeItem(item.eventId);
        } catch (err) {
            console.error("Failed to reject email enrichment:", err);
        } finally {
            setActionLoading(null);
        }
    };

    const handlePurge = async (item: EmailEnrichmentTodoItem) => {
        setActionLoading(`${item.eventId}-purge`);
        try {
            await purgeEnrichmentSuggestion(item.eventId, { enrichmentType: "website_content_OTS" });
            removeItem(item.eventId);
        } catch (err) {
            console.error("Failed to purge email enrichment:", err);
        } finally {
            setActionLoading(null);
        }
    };

    const toggleBusiness = (eventId: string, idx: number) => {
        setBusinessSel((prev) => ({ ...prev, [eventId]: prev[eventId] === idx ? -1 : idx }));
    };

    const toggleOwner = (eventId: string, idx: number) => {
        setOwnerSel((prev) => {
            const next = prev[eventId] === idx ? -1 : idx;
            if (next === -1) setOwnerPersonSel((p) => { const n = { ...p }; delete n[eventId]; return n; });
            return { ...prev, [eventId]: next };
        });
    };

    const selectOwnerPerson = (eventId: string, personId: string) => {
        setOwnerPersonSel((prev) => ({
            ...prev,
            [eventId]: prev[eventId] === personId ? "" : personId,
        }));
    };

    const resetFilters = () => {
        setDateFrom(""); setDateTo(""); setMinScore(undefined); setPage(1);
    };

    const filtersActive = dateFrom || dateTo || minScore !== undefined;

    if (loading) {
        return (
            <div className="flex justify-center py-12">
                <div className="animate-spin h-6 w-6 border-b-2 border-gray-900" />
            </div>
        );
    }

    if (items.length === 0 && !filtersActive) {
        return (
            <div className="text-center py-12 text-gray-500">
                <Mail className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                <p className="text-sm">Aucun email à valider</p>
                <p className="text-xs mt-1 text-gray-400">
                    Les emails trouvés apparaîtront ici pour être classés.
                </p>
            </div>
        );
    }

    return (
        <div className="p-4">
            {/* Filter bar */}
            <div className="flex flex-col gap-2 mb-4 pb-3 border-b border-gray-200">
                {/* Row 1: date + score */}
                <div className="flex flex-wrap items-center gap-3">
                    {/* Date range */}
                    <div className="flex items-center gap-1.5">
                        <input
                            type="date"
                            value={dateFrom}
                            onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
                            className="border border-gray-300 text-xs px-2 py-1 text-gray-700 focus:outline-none focus:border-gray-500"
                        />
                        <span className="text-xs text-gray-400">→</span>
                        <input
                            type="date"
                            value={dateTo}
                            onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
                            className="border border-gray-300 text-xs px-2 py-1 text-gray-700 focus:outline-none focus:border-gray-500"
                        />
                        {(dateFrom || dateTo) && (
                            <button
                                onClick={() => { setDateFrom(""); setDateTo(""); setPage(1); }}
                                className="text-xs text-gray-400 hover:text-gray-600 px-1"
                            >
                                ✕
                            </button>
                        )}
                    </div>

                    {/* Score threshold */}
                    <div className="flex items-center gap-1">
                        <span className="text-xs text-gray-500 mr-0.5">Score :</span>
                        {SCORE_THRESHOLDS.map((t) => (
                            <button
                                key={t.label}
                                onClick={() => { setMinScore(t.value); setPage(1); }}
                                className={`px-2 py-0.5 text-xs border transition-colors ${
                                    minScore === t.value
                                        ? "bg-gray-900 text-white border-gray-900"
                                        : "bg-white text-gray-600 border-gray-200 hover:border-gray-400"
                                }`}
                            >
                                {t.label}
                            </button>
                        ))}
                    </div>

                    <div className="flex items-center gap-2 ml-auto">
                        <p className="text-xs text-gray-500">
                            <span className="font-semibold text-gray-900">{total}</span> email{total !== 1 ? "s" : ""} à valider
                        </p>
                        {filtersActive && (
                            <button
                                onClick={resetFilters}
                                className="text-xs text-gray-400 hover:text-gray-700 underline"
                            >
                                Réinitialiser
                            </button>
                        )}
                    </div>
                </div>

                {/* Row 2: month shortcuts */}
                <div className="flex items-center gap-1 flex-wrap">
                    {Array.from({ length: 8 }, (_, i) => {
                        const d = new Date();
                        d.setDate(1);
                        d.setMonth(d.getMonth() - i);
                        const year = d.getFullYear();
                        const month = d.getMonth();
                        const label = d.toLocaleDateString("fr-FR", { month: "short", year: "2-digit" });
                        const from = `${year}-${String(month + 1).padStart(2, "0")}-01`;
                        const lastDay = new Date(year, month + 1, 0).getDate();
                        const to = `${year}-${String(month + 1).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
                        const isActive = dateFrom === from && dateTo === to;
                        return (
                            <button
                                key={from}
                                onClick={() => { setDateFrom(from); setDateTo(to); setPage(1); }}
                                className={`px-2 py-0.5 text-[10px] font-medium border transition-colors ${
                                    isActive
                                        ? "bg-gray-800 text-white border-gray-800"
                                        : "bg-white text-gray-600 border-gray-300 hover:border-gray-400"
                                }`}
                            >
                                {label}
                            </button>
                        );
                    })}
                </div>
            </div>

            {items.length === 0 && (
                <div className="text-center py-8 text-gray-400 text-xs">
                    Aucun résultat pour ces filtres
                </div>
            )}

            <div className="space-y-3">
                {items.map((item) => {
                    const allOpts = buildEmailOptions(item);
                    const bIdx = businessSel[item.eventId] ?? -1;
                    const oIdx = ownerSel[item.eventId] ?? -1;
                    const selectedPersonId = ownerPersonSel[item.eventId] ?? "";
                    const hasPeople = (item.people?.length ?? 0) > 0;
                    const ownerNeedsPerson = oIdx >= 0 && hasPeople && !selectedPersonId;
                    const hasSelection = (bIdx >= 0 || oIdx >= 0) && !ownerNeedsPerson;
                    const isAnyLoading = actionLoading?.startsWith(item.eventId);

                    return (
                        <div
                            key={item.eventId}
                            className="bg-white border border-gray-200 hover:border-gray-300 transition-colors overflow-hidden"
                        >
                            <div className="flex">
                                {/* Main content */}
                                <div className="flex-1 p-4">
                                    {/* Company header */}
                                    <div className="flex items-start gap-3 mb-3">
                                        <div className="w-8 h-8 bg-gray-100 flex items-center justify-center flex-shrink-0">
                                            <Store className="w-4 h-4 text-gray-500" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <h3 className="font-semibold text-gray-900 text-sm">
                                                    {item.companyName}
                                                </h3>
                                                {item.sourceUrl && (
                                                    <a
                                                        href={item.sourceUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-gray-400 hover:text-gray-600"
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        <ExternalLink className="w-3.5 h-3.5" />
                                                    </a>
                                                )}
                                                {item.eventType && (
                                                    <span className="text-[10px] font-medium px-1.5 py-0.5 bg-gray-100 text-gray-600 border border-gray-200">
                                                        {item.eventType}
                                                    </span>
                                                )}
                                            </div>
                                            {item.people && item.people.length > 0 && (
                                                <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                                                    {item.people.map((p, i) => (
                                                        <span key={i} className="flex items-center gap-1 text-xs text-gray-500">
                                                            <User className="w-3 h-3 flex-shrink-0" />
                                                            {p.name}
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                            <div className="flex items-center gap-2 mt-0.5">
                                                <span className="text-xs text-gray-400">{item.source}</span>
                                                {item.detectedAt && (
                                                    <span className="text-xs text-gray-400">
                                                        · {getDaysAgo(item.detectedAt)}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Website */}
                                    {item.websiteUrl && (
                                        <div className="mb-2 flex items-center gap-2">
                                            <a
                                                href={item.websiteUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="flex items-center gap-1.5 text-xs text-gray-600 hover:text-gray-900"
                                            >
                                                <Globe className="w-3.5 h-3.5 flex-shrink-0" />
                                                <span className="truncate max-w-[240px]">{item.websiteUrl}</span>
                                                <ExternalLink className="w-3 h-3 flex-shrink-0" />
                                            </a>
                                            <span className={`text-[10px] font-medium px-1.5 py-0.5 border ${
                                                item.websiteStatus === "approved"
                                                    ? "bg-green-50 border-green-200 text-green-700"
                                                    : "bg-amber-50 border-amber-200 text-amber-700"
                                            }`}>
                                                {item.websiteStatus}
                                            </span>
                                        </div>
                                    )}

                                    {/* Email options */}
                                    {allOpts.length === 0 ? (
                                        <span className="text-xs text-gray-400 italic">Données email non disponibles</span>
                                    ) : (
                                        <div className="flex flex-col gap-1">
                                            {allOpts.map((opt, i) => {
                                                const emailStr = extractEmailString(opt);
                                                const score = (opt as any).score;
                                                const isOwner = (opt as any).isOwnerEmail;
                                                const isBusiness = bIdx === i;
                                                const isPersonal = oIdx === i;

                                                return (
                                                    <div
                                                        key={i}
                                                        className={`flex items-center gap-2 px-2 py-1.5 border transition-colors ${
                                                            isBusiness
                                                                ? "border-gray-900 bg-gray-50"
                                                                : isPersonal
                                                                ? "border-violet-400 bg-violet-50"
                                                                : "border-gray-200 bg-white"
                                                        }`}
                                                    >
                                                        <ScoreBadge score={score} />
                                                        {isOwner && (
                                                            <span className="text-[10px] font-semibold px-1 py-0.5 bg-amber-50 text-amber-700 border border-amber-200">
                                                                owner
                                                            </span>
                                                        )}
                                                        <span className="flex-1 text-xs font-medium text-gray-800 truncate">
                                                            {emailStr || JSON.stringify(opt).slice(0, 40)}
                                                        </span>
                                                        <button
                                                            onClick={() => toggleBusiness(item.eventId, i)}
                                                            title="Email entreprise"
                                                            className={`flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-semibold border transition-colors ${
                                                                isBusiness
                                                                    ? "bg-gray-900 text-white border-gray-900"
                                                                    : "bg-white text-gray-500 border-gray-300 hover:border-gray-600"
                                                            }`}
                                                        >
                                                            {isBusiness && <Check className="w-2.5 h-2.5" />}
                                                            B
                                                        </button>
                                                        <button
                                                            onClick={() => toggleOwner(item.eventId, i)}
                                                            title="Email propriétaire"
                                                            className={`flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-semibold border transition-colors ${
                                                                isPersonal
                                                                    ? "bg-violet-600 text-white border-violet-600"
                                                                    : "bg-white text-gray-500 border-gray-300 hover:border-violet-400"
                                                            }`}
                                                        >
                                                            {isPersonal && <Check className="w-2.5 h-2.5" />}
                                                            P
                                                        </button>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}

                                    {/* Person picker — shown when an owner email is selected */}
                                    {oIdx >= 0 && hasPeople && (
                                        <div className="mt-2 flex flex-wrap items-center gap-1.5">
                                            <span className="text-[10px] text-gray-500 font-medium">Propriétaire :</span>
                                            {item.people!.map((p) => {
                                                const selected = selectedPersonId === p.id;
                                                return (
                                                    <button
                                                        key={p.id}
                                                        onClick={() => selectOwnerPerson(item.eventId, p.id)}
                                                        className={`flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium border transition-colors ${
                                                            selected
                                                                ? "bg-violet-600 text-white border-violet-600"
                                                                : "bg-white text-gray-600 border-gray-300 hover:border-violet-400"
                                                        }`}
                                                    >
                                                        <User className="w-2.5 h-2.5" />
                                                        {p.name}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>

                                {/* Action panel */}
                                <div className="flex flex-col gap-1 p-3 border-l border-gray-200 bg-gray-50 min-w-[140px] justify-center">
                                    <button
                                        disabled={!hasSelection || !!isAnyLoading}
                                        onClick={() => handleApprove(item)}
                                        className={`flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors ${
                                            hasSelection
                                                ? "bg-gray-900 text-white hover:bg-gray-800"
                                                : "bg-gray-200 text-gray-400 cursor-not-allowed"
                                        } disabled:opacity-50`}
                                    >
                                        <Check className="w-3.5 h-3.5 flex-shrink-0" />
                                        Approuver
                                    </button>
                                    <button
                                        disabled={!!isAnyLoading}
                                        onClick={() => handleReject(item)}
                                        className="flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-white text-gray-700 border border-gray-300 hover:border-amber-400 hover:text-amber-700 disabled:opacity-50 transition-colors"
                                    >
                                        <RefreshCw className="w-3.5 h-3.5 flex-shrink-0" />
                                        Retry
                                    </button>
                                    <div className="h-px bg-gray-200 my-0.5" />
                                    <button
                                        disabled={!!isAnyLoading}
                                        onClick={() => handlePurge(item)}
                                        className="flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-white text-gray-700 border border-gray-300 hover:border-red-400 hover:text-red-700 disabled:opacity-50 transition-colors"
                                    >
                                        <Trash2 className="w-3.5 h-3.5 flex-shrink-0" />
                                        Purger
                                    </button>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Pagination */}
            {total > 50 && (
                <div className="flex items-center justify-center gap-2 mt-6">
                    <button
                        disabled={page === 1}
                        onClick={() => setPage((p) => p - 1)}
                        className="px-3 py-1 text-xs border border-gray-300 text-gray-700 hover:border-gray-400 disabled:opacity-40"
                    >
                        Précédent
                    </button>
                    <span className="text-xs text-gray-500">
                        Page {page} / {Math.ceil(total / 50)}
                    </span>
                    <button
                        disabled={page >= Math.ceil(total / 50)}
                        onClick={() => setPage((p) => p + 1)}
                        className="px-3 py-1 text-xs border border-gray-300 text-gray-700 hover:border-gray-400 disabled:opacity-40"
                    >
                        Suivant
                    </button>
                </div>
            )}
        </div>
    );
}
